import libravatarImage from '../modules/libravatar-image.mjs';

const dayDuration = 8.5 * 60 * 60 * 1000;

const swedishNameFixes = {
    asa: 'Åsa',
    goran: 'Göran',
    hakan: 'Håkan',
    soren: 'Sören',
    janarne: 'Jan-Arne',
};

const formatOrganizer = (email) => {
    if (!email) return { name: 'Okänd bokare', identity: null };
    const localPart = email.split('@')[0];
    if (!localPart.includes('.')) {
        const display = localPart.charAt(0).toUpperCase() + localPart.slice(1).toLowerCase();
        return { name: display, identity: localPart.toLowerCase() };
    }
    const parts = localPart.split(/[._]/).filter(Boolean);
    const name = parts
        .map(part => {
            const lower = part.toLowerCase();
            if (swedishNameFixes[lower]) return swedishNameFixes[lower];
            return part
                .split('-')
                .map(sub => sub.charAt(0).toUpperCase() + sub.slice(1).toLowerCase())
                .join('-');
        })
        .join(' ');
    return { name, identity: localPart.toLowerCase() };
};

function Rooms ({bookingsToday: rooms}) {
    const startOfToday = new Date().setHours(8, 30, 0, 0);
    const now = new Date().getTime();
    const in1Minute = now + (1 * 60 * 1000);
    const in25Minutes = now + (25 * 60 * 1000);

    // Helper function to check if a room has conflicts in the next 25 minutes
    const checkRoomAvailability = (room) => {
        // Process bookings using the exact same logic as rendering
        const processedBookings = room.bookings.map(booking => {
            // Use the exact same date handling as the rendering code
            let startTime = booking.start;
            let endTime = booking.end;

            // Handle all-day events (same logic as in rendering)
            // All-day events are stored as YYYY-MM-DDT00:00:00Z (UTC) which becomes wrong during winter time
            if(new Date(endTime) - new Date(startTime) === 86400000 && startTime.endsWith('Z') && endTime.endsWith('Z')) {
                endTime = endTime.slice(0, -1);
                startTime = startTime.slice(0, -1);
            }

            // Parse dates exactly as done in rendering - this ensures timezone consistency
            const bookingStart = new Date(startTime).getTime();
            const bookingEnd = new Date(endTime).getTime();

            return {
                start: bookingStart,
                end: bookingEnd
            };
        });

        // Check if any booking overlaps with the period from in1Minute to in25Minutes
        // A booking overlaps if it intersects with [in1Minute, in25Minutes]
        // We use in1Minute instead of now to count rooms with meetings ending in the next minute as available
        // Overlap condition: booking starts before in25Minutes AND ends after in1Minute
        const hasConflict = processedBookings.some(booking => {
            // A booking blocks availability if:
            // - It starts during our window (in1Minute < start < in25Minutes)
            // - It spans our window (start <= in1Minute < in25Minutes <= end)
            // - It's ongoing but ends after our window starts (start < in1Minute < end < in25Minutes)
            const overlaps = booking.start < in25Minutes && booking.end > in1Minute;

            // Debug logging for ongoing meetings
            if (booking.start <= now && booking.end >= now) {
                console.log(`Room ${room.displayName}: Ongoing meeting detected`, {
                    now: new Date(now).toLocaleString('sv-SE'),
                    bookingStart: new Date(booking.start).toLocaleString('sv-SE'),
                    bookingEnd: new Date(booking.end).toLocaleString('sv-SE'),
                    overlaps: overlaps,
                    condition1: booking.start < in25Minutes,
                    condition2: booking.end > now
                });
            }

            return overlaps;
        });

        return !hasConflict;
    };

    // Find rooms with available slots in the next 25 minutes
    const availableRooms = rooms.filter(room => checkRoomAvailability(room));

    const rightNowPosition = (new Date().getTime() - startOfToday) / dayDuration * 100;
    const clampedPosition = Math.min(Math.max(rightNowPosition, 0), 100);

    return (
        <div
            className = "rooms"
        >
            {availableRooms.length > 0 && (
                <div className="available-rooms">
                    <h3>Tillgängliga rum för ett kort möte:</h3>
                    <div className="available-rooms-links">
                        {availableRooms.map(room => (
                            <a
                                key={room.identifier}
                                href={`https://intranet.stendahls.dev/praktisk-information/vad-finns-var/kartor/?vaning=${room.identifier.substring(0, 1)}&typ=motesrum`}
                                className="available-room-link"
                            >
                                {room.displayName} - {room.identifier.substring(17, 20)}
                            </a>
                        ))}
                    </div>
                </div>
            )}
            <div className="rooms-timeline-container">
                <div
                    className = "right-now-line"
                    style = {{
                        '--time-position': clampedPosition,
                    }}
                />

                {rooms.map(room => {
                return <div
                    key = {room.identifier}
                    className = "room-row"
                >
                    <h2>
                        <a
                            href = {`https://intranet.stendahls.dev/praktisk-information/vad-finns-var/kartor/?vaning=${room.identifier.substring(0, 1)}&typ=motesrum`}
                        >
                            {room.displayName} - {room.identifier.substring(17, 20)}
                        </a>
                    </h2>
                    <div
                        className = "room-wrapper"
                    >
                    {room.bookings.map(booking => {
                        if(new Date(booking.end) - new Date(booking.start) === 86400000 && booking.start.endsWith('Z') && booking.end.endsWith('Z')) {
                            // All-day events are stored as YYYY-MM-DDT00:00:00Z (UTC) which becomes 1 hour wrong during winter (normal) time. Getting rid of the Z fixes this!
                            booking.end = booking.end.slice(0, -1);
                            booking.start = booking.start.slice(0, -1);
                        }
                        const startPosition = (new Date(booking.start).getTime() - startOfToday) / dayDuration * 100;
                        const width = (new Date(booking.end).getTime() - new Date(booking.start).getTime()) / dayDuration * 100;

                        if(startPosition > 100){
                            console.log(booking);

                            return null;
                        }

                        const startTime = new Date(booking.start).toLocaleTimeString('sv-SE', { timeStyle: 'short' });
                        const endTime = new Date(booking.end).toLocaleTimeString('sv-SE', { timeStyle: 'short' });

                        const mailtoHref = `mailto:${booking.organizer}?subject=Angående%20din%20rumsbokning%20i%20${room.displayName}%20(${room.identifier.substring(0, 3)})%20kl%20${startTime}`;

                        const { name: organizerName, identity } = formatOrganizer(booking.organizer);
                        const avatarUrl = identity ? libravatarImage(`${identity}@stendahls.se`, 96) : null;

                        const rawSubject = (booking.subject || '').trim();
                        const normalize = (s) => s.toLowerCase().replace(/\s+/g, ' ').trim();
                        const subjectIsJustName = rawSubject && normalize(rawSubject) === normalize(organizerName);
                        const displaySubject = rawSubject && !subjectIsJustName ? rawSubject : null;

                        const durationMs = new Date(booking.end).getTime() - new Date(booking.start).getTime();
                        const durationMinutes = Math.round(durationMs / 60000);
                        const durationLabel = durationMinutes >= 60
                            ? `${Math.floor(durationMinutes / 60)} h${durationMinutes % 60 ? ` ${durationMinutes % 60} min` : ''}`
                            : `${durationMinutes} min`;

                        const clampedLeft = Math.max(startPosition, 0);
                        const clampedWidth = Math.min(width, 100 - clampedLeft);

                        return <div
                            className = "booking-wrapper"
                            key = {`${booking.roomIdentifier}-${booking.start}`}
                            style = {{
                                left: `${clampedLeft}%`,
                                width: `${clampedWidth}%`,
                            }}
                        >
                            <a href={mailtoHref}>
                                <span className="tooltiptext" role="tooltip">
                                    <span className="tooltip-header">
                                        {avatarUrl && (
                                            <span
                                                className="tooltip-avatar"
                                                style={{ backgroundImage: `url(${avatarUrl})` }}
                                                aria-hidden="true"
                                            />
                                        )}
                                        <span className="tooltip-identity">
                                            <span className="tooltip-name">{organizerName}</span>
                                            {booking.organizer && (
                                                <span className="tooltip-email">{booking.organizer}</span>
                                            )}
                                        </span>
                                    </span>
                                    {displaySubject && (
                                        <span className="tooltip-subject">
                                            <span className="tooltip-subject-label">Ämne</span>
                                            <span className="tooltip-subject-text">{displaySubject}</span>
                                        </span>
                                    )}
                                    <span className="tooltip-meta">
                                        <span className="tooltip-time">
                                            {startTime}<span className="tooltip-dash">–</span>{endTime}
                                        </span>
                                        <span className="tooltip-duration">{durationLabel}</span>
                                    </span>
                                    <span className="tooltip-hint">
                                        <span className="tooltip-hint-icon" aria-hidden="true">✉</span>
                                        Klicka för att mejla
                                    </span>
                                </span>
                                <span style={{ display: 'block'}}>
                                    {startTime}
                                </span>
                                <span style={{ display: 'block'}}>
                                    {'-'}
                                </span>
                                <span style={{ display: 'block'}}>
                                    {endTime}
                                </span>
                            </a>
                        </div>;
                    })}
                    </div>
                </div>;
                })}
            </div>
        </div>
    )
}

export default Rooms;
